/**
 * API service wrapper for making HTTP requests
 * Uses the native fetch API with proper headers and error handling
 */

import { useAlertStore } from '@/stores/alerts-store';

export interface ApiResponse<T = any> extends Response {
  data?: T;
}

// Custom error class for network/connection errors
export class ConnectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConnectionError';
  }
}

// Custom error class for HTTP errors (4xx, 5xx)
export class HttpError extends Error {
  public status: number;
  public statusText: string;
  public response?: any;

  constructor(status: number, statusText: string, response?: any) {
    super(`HTTP ${status}: ${statusText}`);
    this.name = 'HttpError';
    this.status = status;
    this.statusText = statusText;
    this.response = response;
  }
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || '';
  }

  private async request<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers,
      },
      credentials: 'include',
    };

    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;

    try {
      const response = await fetch(fullUrl, requestOptions);
      return response;
    } catch (error) {
      // Handle network errors (e.g., API not running, connection refused, DNS failures)
      console.error('API request failed:', error);
      const alertStore = useAlertStore();
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      const userFriendlyMessage = 'Unable to connect to the server. Please check your connection or try again later.';

      alertStore.addError('Connection Error', userFriendlyMessage);

      // Throw a custom ConnectionError so components can differentiate
      throw new ConnectionError(userFriendlyMessage);
    }
  }

  async get<T = any>(url: string, options: RequestInit = {}): Promise<Response> {
    return this.request<T>(url, {
      ...options,
      method: 'GET',
    });
  }

  async post<T = any>(
    url: string,
    data?: any,
    options: RequestInit = {}
  ): Promise<Response> {
    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(
    url: string,
    data?: any,
    options: RequestInit = {}
  ): Promise<Response> {
    return this.request<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(url: string, options: RequestInit = {}): Promise<Response> {
    return this.request<T>(url, {
      ...options,
      method: 'DELETE',
    });
  }

  async patch<T = any>(
    url: string,
    data?: any,
    options: RequestInit = {}
  ): Promise<Response> {
    return this.request<T>(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

const api = new ApiService();
export default api;
